/**
 * The name of every tool, in one place — read by the tool's own declaration and by anything that
 * mentions it: system prompts, skill instructions, schema descriptions, tool results, lint
 * findings, a call argument carrying a tool name.
 *
 * This module imports nothing, so a name is readable at module-eval time from anywhere,
 * including a module that sits in an import cycle.
 *
 * A key is the camelCase of its value, not of the declaring class: a legacy/current pair emits
 * one name between them, and the entry follows the name. Where more than one class declares an
 * entry, a comment above it names them.
 */
export const TOOL_NAMES = {
    addAvatarSceneGroup: 'add_avatar_scene_group',
    addFonts: 'add_fonts',
    addIcon: 'add_icon',
    addRecordedVoiceoverSceneGroup: 'add_recorded_voiceover_scene_group',
    // AddSceneTool, LegacyAddSceneTool
    addScene: 'add_scene',
    // AddStickerTool, LegacyAddStickerTool
    addSticker: 'add_sticker',
    addSubscene: 'add_subscene',
    addTalkingHeadSceneGroup: 'add_talking_head_scene_group',
    // AddVoiceoverSceneTool, LegacyAddVoiceoverSceneTool
    addVoiceoverScene: 'add_voiceover_scene',
    // AddAIVoiceoverSceneGroupTool, LegacyAddAIVoiceoverSceneGroupTool
    addVoiceoverSceneGroup: 'add_voiceover_scene_group',
    analyzeClipFraming: 'analyze_clip_framing',
    analyzeVideo: 'analyze_video',
    applyBrandKit: 'apply_brand_kit',
    askUserQuestion: 'ask_user_question',
    checkCapability: 'check_capability',
    cleanUpRecording: 'clean_up_recording',
    commitPlan: 'commit_plan',
    createBlankVideo: 'create_blank_video',
    createVideo: 'create_video',
    createVideoFromPlan: 'create_video_from_plan',
    createVideoFromScript: 'create_video_from_script',
    createVideoFromVoiceoverAsset: 'create_video_from_voiceover_asset',
    createVideoFromVoiceoverAssetPlan: 'create_video_from_voiceover_asset_plan',
    createVoiceover: 'create_voiceover',
    // DeleteSceneTool, LegacyDeleteSceneTool
    deleteScene: 'delete_scene',
    deleteSceneGroup: 'delete_scene_group',
    documentExtract: 'document_extract',
    editAiVoiceover: 'edit_ai_voiceover',
    editComposition: 'edit_composition',
    editHtmlAsset: 'edit_html_asset',
    editMedia: 'edit_media',
    editPrompt: 'edit_prompt',
    extractRawBrandData: 'extract_raw_brand_data',
    fetchReferenceVideo: 'fetch_reference_video',
    fetchUrlContent: 'fetch_url_content',
    findTemplates: 'find_templates',
    findWaveformSceneDesign: 'find_waveform_scene_design',
    fixPronunciation: 'fix_pronunciation',
    generateAiMedia: 'generate_ai_media',
    getBrandFrame: 'get_brand_frame',
    getSceneScreenshotWithDescription: 'get_scene_screenshot_with_description',
    getVideo: 'get_video',
    getVideoState: 'get_video_state',
    // ImageAskTool, LegacyImageAskTool
    imageAsk: 'image_ask',
    lintVideo: 'lint_video',
    listAvailableMedia: 'list_available_media',
    listAvatars: 'list_avatars',
    listBrandKits: 'list_brand_kits',
    // ListSceneDesignsTool, ListSceneDesignTool
    listSceneDesigns: 'list_scene_designs',
    loadFormat: 'load_format',
    // codeo/LoadSkillTool, tools/LoadSkillTool
    loadSkill: 'load_skill',
    moveSceneGroup: 'move_scene_group',
    previewBrandIcons: 'preview_brand_icons',
    proposeClips: 'propose_clips',
    readHtmlAsset: 'read_html_asset',
    readPrompt: 'read_prompt',
    rebuildVideo: 'rebuild_video',
    replaceIconColors: 'replace_icon_colors',
    reportFindings: 'report_findings',
    researchTopic: 'research_topic',
    reviewSceneImages: 'review_scene_images',
    reviewVideoFrames: 'review_video_frames',
    runSubagent: 'run_subagent',
    saveBrandKit: 'save_brand_kit',
    searchDamMedia: 'search_dam_media',
    // SearchIconsTool, IconSearchTool
    searchIcons: 'search_icons',
    searchMedia: 'search_media',
    // SearchMusicTool, MusicSearchTool
    searchMusic: 'search_music',
    searchUploadedVoiceovers: 'search_uploaded_voiceovers',
    searchVoices: 'search_voices',
    setAspectRatio: 'set_aspect_ratio',
    setAssetColor: 'set_asset_color',
    setAssetTiming: 'set_asset_timing',
    // LegacySetBackgroundTool
    setBackground: 'set_background',
    setCaptions: 'set_captions',
    setColorPalette: 'set_color_palette',
    setGlobalColorGrading: 'set_global_color_grading',
    // SetMediaEffectsTool
    setMediaEffects: 'set_media_effects',
    // LegacySetAssetGroupTool, SetAssetGroupTool
    setGroupAsset: 'set_group_asset',
    // LegacySetIconTool, SetIconTool
    setIcon: 'set_icon',
    setSceneTransition: 'set_scene_transition',
    // LegacySetMediaTool, SetMediaTool
    setMedia: 'set_media',
    // codeo/SetMusicTool, tools/SetMusicTool
    setMusic: 'set_music',
    setMusicVolume: 'set_music_volume',
    setSceneGroupVolume: 'set_scene_group_volume',
    // LegacySetSceneDurationTool, tools/SetSceneDurationTool
    setSceneDuration: 'set_scene_duration',
    setSceneRange: 'set_scene_range',
    // LegacySetStickerTool, SetStickerTool
    setSticker: 'set_sticker',
    setTemplateColor: 'set_template_color',
    // LegacySetTextTool, SetTextTool
    setText: 'set_text',
    setTextColor: 'set_text_color',
    // SetTextAnimationTool
    setTextAnimation: 'set_text_animation',
    // LegacySetTextPositionPresetTool, SetTextPositionPresetTool
    setTextPositionPreset: 'set_text_position_preset',
    // LegacySetWatermarkTool, SetWatermarkTool
    setWatermark: 'set_watermark',
    setWaveformStyle: 'set_waveform_style',
    splitSceneGroup: 'split_scene_group',
    suggestAnimatedExplainer: 'suggest_animated_explainer',
    suggestUpgrade: 'suggest_upgrade',
    translateVideo: 'translate_video',
    trimSceneGroup: 'trim_scene_group',
    validateContrast: 'validate_contrast',
    webImageSearch: 'web_image_search',
    writeComposition: 'write_composition',
    // html-design/WriteDesignSystemTool
    writeDesignSystem: 'write_design_system',
    writeHtmlAsset: 'write_html_asset',
    // html-design/WritePlanTool
    writePlan: 'write_plan',
    // html-design/chart/WriteSceneHtmlTool
    writeSceneHtml: 'write_scene_html',
} as const;

export type ToolName = (typeof TOOL_NAMES)[keyof typeof TOOL_NAMES];
